import React, { useState, useEffect, useRef } from 'react';
import './AccessibleCarousel.css';
import prevButton from "../../assets/images/prevButton.svg";
import nextButton from "../../assets/images/nextButton.svg";

const AccessibleCarousel = ({ items = [], timeInterval = 5000, customClass }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(1);
  const [hasInteracted, setHasInteracted] = useState(false); // track user interaction

  const containerRef = useRef(null);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);

  // update number of items shown based on container width
  const updateItemsToShow = () => {
    const width = containerRef.current?.offsetWidth || 0;

    if (width >= 900) setItemsToShow(5);
    else if (width >= 836) setItemsToShow(4);
    else if (width >= 768) setItemsToShow(3);
    else setItemsToShow(3);
  };

  useEffect(() => {
    updateItemsToShow();
    window.addEventListener('resize', updateItemsToShow);
    return () => window.removeEventListener('resize', updateItemsToShow);
  }, []);

  const handlePrevious = () => {
    setHasInteracted(true);
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setHasInteracted(true);
    const maxIndex = Math.max(items.length - itemsToShow, 0);
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  // focus shift only AFTER interaction
  useEffect(() => {
    if (!hasInteracted) return;

    const maxIndex = Math.max(items.length - itemsToShow, 0);

    if (currentIndex === 0 && prevButtonRef.current?.disabled) {
      nextButtonRef.current?.focus();
    } else if (currentIndex >= maxIndex && nextButtonRef.current?.disabled) {
      prevButtonRef.current?.focus();
    }
  }, [currentIndex, items.length, itemsToShow, hasInteracted]);

  return (
    <section
      className={`carousel ${customClass || ''}`}
      aria-roledescription="carousel"
      aria-label="Item"
    >
      <div className="carousel-inner" ref={containerRef}>
        <button
          type="button"
          className="previous"
          aria-label="Previous"
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          ref={prevButtonRef}
          tabIndex={currentIndex === 0 ? -1 : 0} // remove from tab order if disabled
          // aria-hidden={currentIndex === 0 ? "true" : "false"}
        >
          <img src={prevButton} alt="Previous" />
        </button>

        <div className="carousel-items" role="list">
          <div
            className="carousel-track"
            style={{
              transform: `translateX(-${(100 / itemsToShow) * currentIndex}%)`,
              gridTemplateColumns: `repeat(${items.length}, ${100 / itemsToShow}%)`,
            }}
          >
           {items.map((item, index) => {
              const isVisible = index >= currentIndex && index < currentIndex + itemsToShow;

              return (
                <div
                  key={index}
                  className="carousel-item"
                  role="listitem"
                  data-testid="carousel-slide"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${items.length}`}
                  aria-hidden={!isVisible}
                  // tabIndex={isVisible ? 0 : -1}
                  style={{ display: isVisible ? 'block' : 'none' }} // or visibility:hidden
                >
                  <img src={item.icon} alt="" aria-hidden="true" />
                  <div className="carousel-caption">{item.name}</div>
                </div>
              );
            })}

          </div>
        </div>

        <button
          type="button"
          className="next"
          aria-label="Next click enter for more images"
          onClick={handleNext}
          disabled={currentIndex >= items.length - itemsToShow}
          ref={nextButtonRef}
          tabIndex={currentIndex >= items.length - itemsToShow ? -1 : 0}
          aria-hidden={currentIndex >= items.length - itemsToShow ? "true" : "false"}
        >
          <img src={nextButton} alt="Next" />
        </button>
      </div>
    </section>
  );
};

export default AccessibleCarousel;
